import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { suppliersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Ship, LogOut, LayoutDashboard, Package, Users,
  ArrowLeft, Loader2, Building2, Globe, Mail, Phone,
  MapPin, ChevronRight, Plane, Truck, TrendingUp, TrendingDown
} from 'lucide-react';
import { toast } from 'sonner';
import { getStatusColor, getStatusLabel, formatDate, formatCurrency } from '../lib/utils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';

function Sidebar({ onLogout }) {
  return (
    <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 p-6">
      <Link to="/dashboard" className="flex items-center gap-2 mb-8">
        <div className="w-8 h-8 bg-importdocs-primary rounded-md flex items-center justify-center">
          <Ship className="w-5 h-5 text-white" />
        </div>
        <span className="font-heading font-bold text-xl text-importdocs-text">ImportDocs</span>
      </Link>
      
      <nav className="flex-1 space-y-1">
        <Link
          to="/dashboard"
          className="flex items-center gap-3 px-4 py-3 rounded-md text-importdocs-text-secondary hover:bg-slate-100"
        >
          <LayoutDashboard className="w-5 h-5" />
          Dashboard
        </Link>
        <Link
          to="/dashboard/shipments"
          className="flex items-center gap-3 px-4 py-3 rounded-md text-importdocs-text-secondary hover:bg-slate-100"
        >
          <Package className="w-5 h-5" />
          Shipments
        </Link>
        <Link
          to="/dashboard/suppliers"
          className="flex items-center gap-3 px-4 py-3 rounded-md bg-importdocs-primary text-white"
        >
          <Users className="w-5 h-5" />
          Suppliers
        </Link>
      </nav>
      
      <Button 
        variant="ghost" 
        className="justify-start text-importdocs-text-secondary hover:text-importdocs-danger"
        onClick={onLogout}
      >
        <LogOut className="w-5 h-5 mr-2" />
        Logout
      </Button>
    </aside>
  );
}

function ShipmentRow({ shipment }) {
  const TransportIcon = shipment.transport_type === 'Sea' ? Ship : 
                        shipment.transport_type === 'Air' ? Plane : Truck;
  
  return (
    <Link to={`/dashboard/shipments/${shipment.id}`}>
      <div className="flex items-center gap-4 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`shipment-row-${shipment.id}`}>
        <div className="w-10 h-10 rounded-lg bg-importdocs-subtle flex items-center justify-center flex-shrink-0">
          <TransportIcon className="w-5 h-5 text-importdocs-primary" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-importdocs-text truncate">{shipment.name}</h3>
            <Badge className={`${getStatusColor(shipment.status)} border text-xs`}>
              {getStatusLabel(shipment.status)}
            </Badge>
          </div>
          <div className="flex items-center gap-4 text-sm text-importdocs-text-secondary mt-1">
            <span className="font-mono">{shipment.reference}</span>
            <span>{shipment.country_of_origin}</span>
          </div>
        </div>
        
        <div className="hidden md:flex items-center gap-8">
          <div className="text-right">
            <p className="text-xs text-importdocs-text-muted">Created</p>
            <p className="text-sm text-importdocs-text">{formatDate(shipment.created_at)}</p>
          </div>
          <div className="w-24">
            <p className="text-xs text-importdocs-text-muted mb-1">Docs {shipment.document_completion}%</p>
            <Progress value={shipment.document_completion} className="h-2" />
          </div>
        </div>
        
        <ChevronRight className="w-5 h-5 text-importdocs-text-muted" />
      </div>
    </Link>
  );
}

function CostChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-importdocs-text-muted">
        No shipment data to display
      </div>
    );
  }

  const chartData = data.map((item, index) => ({
    name: `#${index + 1}`,
    fullName: item.shipment_name,
    estimated: item.estimated_total,
    actual: item.actual_total,
    date: formatDate(item.date),
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
        <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
        <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: 'white', 
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
          formatter={(value) => formatCurrency(value)}
          labelFormatter={(label, payload) => payload[0]?.payload?.fullName || label}
        />
        <Legend />
        <Bar dataKey="estimated" name="Estimated" fill="#94A3B8" radius={[4, 4, 0, 0]} />
        <Bar dataKey="actual" name="Actual" fill="#1F3A5F" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function CostBreakdownChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-importdocs-text-muted">
        No cost data to display
      </div>
    );
  }

  const chartData = data.map((item, index) => ({
    name: `#${index + 1}`,
    fob: item.fob,
    freight: item.freight,
    insurance: item.insurance,
    duties: item.duties,
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
        <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
        <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${v}`} />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: 'white', 
            border: '1px solid #E2E8F0',
            borderRadius: '8px'
          }}
          formatter={(value) => formatCurrency(value)}
        />
        <Legend />
        <Line type="monotone" dataKey="fob" name="FOB" stroke="#1F3A5F" strokeWidth={2} dot={{ fill: '#1F3A5F' }} />
        <Line type="monotone" dataKey="freight" name="Freight" stroke="#F97316" strokeWidth={2} dot={{ fill: '#F97316' }} />
        <Line type="monotone" dataKey="insurance" name="Insurance" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} />
        <Line type="monotone" dataKey="duties" name="Duties" stroke="#8B5CF6" strokeWidth={2} dot={{ fill: '#8B5CF6' }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function SupplierDetailPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [supplierRes, shipmentsRes, reportRes] = await Promise.all([
        suppliersAPI.getOne(id),
        suppliersAPI.getShipments(id),
        suppliersAPI.getReport(id),
      ]);
      setSupplier(supplierRes.data);
      setShipments(shipmentsRes.data);
      setReport(reportRes.data);
    } catch (error) {
      toast.error('Failed to load supplier data');
      navigate('/dashboard/suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-importdocs-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-importdocs-primary" />
      </div>
    );
  }

  if (!supplier) return null;

  const variance = report?.summary?.variance_percentage || 0;
  const isOverBudget = variance > 0;

  return (
    <div className="min-h-screen bg-importdocs-bg flex">
      <Sidebar onLogout={handleLogout} />
      
      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/suppliers">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-importdocs-primary/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-importdocs-primary" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl text-importdocs-text">{supplier.name}</h1>
                <p className="text-sm text-importdocs-text-muted flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  {supplier.country}
                </p>
              </div>
            </div>
          </div>
        </header>
        
        <main className="flex-1 p-6">
          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <p className="text-sm text-importdocs-text-muted">Total Shipments</p>
                <p className="text-2xl font-bold text-importdocs-text">{report?.summary?.total_shipments || 0}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <p className="text-sm text-importdocs-text-muted">Total Estimated</p>
                <p className="text-2xl font-bold text-importdocs-text font-mono">{formatCurrency(report?.summary?.total_estimated || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <p className="text-sm text-importdocs-text-muted">Total Actual</p>
                <p className="text-2xl font-bold text-importdocs-text font-mono">{formatCurrency(report?.summary?.total_actual || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <p className="text-sm text-importdocs-text-muted">Overall Variance</p>
                <p className={`text-2xl font-bold font-mono flex items-center gap-2 ${isOverBudget ? 'text-importdocs-danger' : 'text-importdocs-success'}`}>
                  {isOverBudget ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                  {variance.toFixed(1)}%
                </p>
              </CardContent>
            </Card>
          </div>
          
          <Tabs defaultValue="shipments" className="space-y-6">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="shipments" data-testid="shipments-tab">Shipments ({shipments.length})</TabsTrigger>
              <TabsTrigger value="report" data-testid="report-tab">Cost Report</TabsTrigger>
              <TabsTrigger value="info" data-testid="info-tab">Supplier Info</TabsTrigger>
            </TabsList>
            
            {/* Shipments Tab */}
            <TabsContent value="shipments">
              <Card className="border-slate-200" data-testid="supplier-shipments-card">
                <CardHeader>
                  <CardTitle className="font-heading">All Shipments</CardTitle>
                  <CardDescription>Shipments from this supplier</CardDescription>
                </CardHeader>
                <CardContent>
                  {shipments.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-12 h-12 text-importdocs-text-muted mx-auto mb-4" />
                      <p className="text-importdocs-text-secondary">No shipments from this supplier yet</p>
                      <Link to="/dashboard/shipments/new">
                        <Button variant="outline" className="mt-4">
                          Create First Shipment
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {shipments.map((shipment) => (
                        <ShipmentRow key={shipment.id} shipment={shipment} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Report Tab */}
            <TabsContent value="report">
              <div className="grid lg:grid-cols-2 gap-6">
                <Card className="border-slate-200" data-testid="cost-comparison-chart">
                  <CardHeader>
                    <CardTitle className="font-heading">Estimated vs Actual Costs</CardTitle>
                    <CardDescription>Cost comparison across shipments</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <CostChart data={report?.time_series} />
                  </CardContent>
                </Card>
                
                <Card className="border-slate-200" data-testid="cost-breakdown-chart">
                  <CardHeader>
                    <CardTitle className="font-heading">Cost Breakdown Over Time</CardTitle>
                    <CardDescription>FOB, Freight, Insurance, Duties trends</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <CostBreakdownChart data={report?.time_series} />
                  </CardContent>
                </Card>
              </div>
              
              {/* Cost Table */}
              <Card className="border-slate-200 mt-6" data-testid="cost-table">
                <CardHeader>
                  <CardTitle className="font-heading">Shipment Cost Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 font-semibold text-importdocs-text">Shipment</th>
                          <th className="text-right py-3 px-4 font-semibold text-importdocs-text">FOB</th>
                          <th className="text-right py-3 px-4 font-semibold text-importdocs-text">Freight</th>
                          <th className="text-right py-3 px-4 font-semibold text-importdocs-text">Insurance</th>
                          <th className="text-right py-3 px-4 font-semibold text-importdocs-text">Duties</th>
                          <th className="text-right py-3 px-4 font-semibold text-importdocs-text">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report?.time_series?.map((item, index) => (
                          <tr key={item.shipment_id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <Link to={`/dashboard/shipments/${item.shipment_id}`} className="text-importdocs-primary hover:underline">
                                {item.shipment_name}
                              </Link>
                              <p className="text-xs text-importdocs-text-muted">{formatDate(item.date)}</p>
                            </td>
                            <td className="text-right py-3 px-4 font-mono">{formatCurrency(item.fob)}</td>
                            <td className="text-right py-3 px-4 font-mono">{formatCurrency(item.freight)}</td>
                            <td className="text-right py-3 px-4 font-mono">{formatCurrency(item.insurance)}</td>
                            <td className="text-right py-3 px-4 font-mono">{formatCurrency(item.duties)}</td>
                            <td className="text-right py-3 px-4 font-mono font-semibold">{formatCurrency(item.actual_total || item.estimated_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                      {report?.time_series?.length > 0 && (
                        <tfoot>
                          <tr className="bg-importdocs-subtle font-semibold">
                            <td className="py-3 px-4">TOTAL</td>
                            <td className="text-right py-3 px-4 font-mono">
                              {formatCurrency(report.time_series.reduce((sum, i) => sum + (i.fob || 0), 0))}
                            </td>
                            <td className="text-right py-3 px-4 font-mono">
                              {formatCurrency(report.time_series.reduce((sum, i) => sum + (i.freight || 0), 0))}
                            </td>
                            <td className="text-right py-3 px-4 font-mono">
                              {formatCurrency(report.time_series.reduce((sum, i) => sum + (i.insurance || 0), 0))}
                            </td>
                            <td className="text-right py-3 px-4 font-mono">
                              {formatCurrency(report.time_series.reduce((sum, i) => sum + (i.duties || 0), 0))}
                            </td>
                            <td className="text-right py-3 px-4 font-mono">
                              {formatCurrency(report.summary?.total_actual || 0)}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Info Tab */}
            <TabsContent value="info">
              <Card className="border-slate-200" data-testid="supplier-info-card">
                <CardHeader>
                  <CardTitle className="font-heading">Supplier Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-importdocs-text-muted">Company Name</p>
                        <p className="text-importdocs-text font-medium">{supplier.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-importdocs-text-muted">Country</p>
                        <p className="text-importdocs-text flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          {supplier.country}
                        </p>
                      </div>
                      {supplier.address && (
                        <div>
                          <p className="text-sm text-importdocs-text-muted">Address</p>
                          <p className="text-importdocs-text flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {supplier.address}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="space-y-4">
                      {supplier.contact_name && (
                        <div>
                          <p className="text-sm text-importdocs-text-muted">Contact Person</p>
                          <p className="text-importdocs-text flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            {supplier.contact_name}
                          </p>
                        </div>
                      )}
                      {supplier.email && (
                        <div>
                          <p className="text-sm text-importdocs-text-muted">Email</p>
                          <p className="text-importdocs-text flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            <a href={`mailto:${supplier.email}`} className="text-importdocs-primary hover:underline">
                              {supplier.email}
                            </a>
                          </p>
                        </div>
                      )}
                      {supplier.phone && (
                        <div>
                          <p className="text-sm text-importdocs-text-muted">Phone</p>
                          <p className="text-importdocs-text flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            {supplier.phone}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  {supplier.notes && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <p className="text-sm text-importdocs-text-muted">Notes</p>
                      <p className="text-importdocs-text mt-1">{supplier.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
